import { type CustomIconName, Icon } from '@/components/Icon/Icon';
import { type Icon as FeatherIcon } from 'react-feather';
import classes from './FloatingIconButton.module.css';

interface BaseIconButtonProps {
  /**
   * Is the button disabled?
   */
  isDisabled?: boolean;
  /**
   * Is the button active?
   */
  isActive?: boolean;
  /**
   * Optional click handler
   */
  onClick?: () => void;
  /**
   * Optional icon color
   */
  color?: string;
  /**
   * Optional additional class names
   */
  className?: string;
}

type IconButtonProps =
  | (BaseIconButtonProps & { name: CustomIconName; Component?: never })
  | (BaseIconButtonProps & { Component: FeatherIcon; name?: never });

/**
 * Floating Icon Button
 */
export const FloatingIconButton = ({
  Component,
  name,
  isDisabled = false,
  isActive = false,
  color = 'black',
  className,
  ...props
}: IconButtonProps) => {
  const buttonClass = isDisabled
    ? classes.buttonDisabled
    : isActive
      ? classes.buttonActive
      : '';
  return (
    <button
      type="button"
      className={`${className ?? ''} ${classes.button} ${buttonClass}`}
      data-testid="floating-icon-button"
      data-state={isDisabled ? 'disabled' : isActive ? 'active' : 'default'}
      {...props}
    >
      {Component && <Icon Component={Component} color={color} size={24} />}
      {name && <Icon name={name} color={color} size={24} />}
    </button>
  );
};
